import React, { useState } from "react";
import {
  Box,
  Button,
  HStack,
  Icon,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { FaArrowLeft, FaHistory, FaSave, FaTimes } from "react-icons/fa";
import JobCreationComponent from "./JobCreationComponent";
import { Header } from "build-ts/metadata";
import showToast from "hooks/useCustomToast";
import { runJobAsync } from "services/ScheduleServices";
import { MetadataTypeEnum, metadataTypeEnumToJSON } from "build-ts/enums";
import AnalysisRunsViewer from "./AnalysisRunsViewer";
import { MdAnalytics } from "react-icons/md";

interface JobViewComponentProps {
  onBack: () => void;
  isCreatingNew?: boolean;
  selectedJob?: Header;
}

const JobViewComponent: React.FC<JobViewComponentProps> = ({
  onBack,
  isCreatingNew = true,
  selectedJob,
}) => {
  // Job name state
  const [jobName, setJobName] = useState<string>("");
  // Active tab state
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [triggerSave, setTriggerSave] = useState<boolean>(false);
  const [hasFormChanges, setHasFormChanges] = useState<boolean>(false);

  const handleSave = () => {
    setTriggerSave(true);
  };

  // Handle run job
  const handleRunJob = async () => {
    if (!selectedJob?.id) return;
    await runJobAsync(selectedJob.id, metadataTypeEnumToJSON(MetadataTypeEnum.ANALYST));
    showToast("Analysis running", "The analysis has been triggered.", "success");
  };

  const handleCancel = () => {
    onBack();
  };

  return (
    <Box height="100%" bg="white" display="flex" flexDirection="column">
      <Box flex="1" overflow="hidden">
        <Tabs
          colorScheme="purple"
          isFitted
          variant="line"
          size="md"
          height="100%"
          index={activeTab}
          onChange={setActiveTab}
        >
          <HStack p={2} py={1} borderBottom="1px solid" borderColor="gray.100" align="center">
            {/* Back */}
            <Button
              leftIcon={<Icon as={FaArrowLeft} boxSize={3} />}
              variant="ghost"
              size="sm"
              onClick={onBack}
              color="gray.700"
              _hover={{ bg: "gray.100" }}
              flexShrink={0}
            >
              Back
            </Button>

            {/* Tab List */}
            <TabList flex="1" border="none">
              <Tab _hover={{ bg: "purple.50" }}>
                <HStack spacing={2}>
                  <Icon as={MdAnalytics} boxSize={5} />
                  <Text mb={0} fontSize="sm" fontWeight="700" noOfLines={1}>
                    {jobName.trim() || (isCreatingNew ? "New Analysis" : "Analysis")}
                  </Text>
                </HStack>
              </Tab>
              <Tab _hover={{ bg: "purple.50" }}>
                <HStack spacing={2}>
                  <Icon as={FaHistory} boxSize={4} />
                  <Text mb={0} fontSize="sm" fontWeight="700">Past Runs</Text>
                </HStack>
              </Tab>
            </TabList>
          </HStack>

          <TabPanels height="calc(100% - 48px)">
            <TabPanel height="100%" p={0}>
              <JobCreationComponent
                jobName={jobName}
                setJobName={setJobName}
                selectedJob={selectedJob}
                isCreatingNew={isCreatingNew}
                triggerSave={triggerSave}
                setTriggerSave={setTriggerSave}
                setIsSaving={setIsSaving}
                onSaveSuccess={onBack}
                setHasFormChanges={setHasFormChanges}
                onRun={handleRunJob}
              />
            </TabPanel>

            {/* ── Past Runs tab ── */}
            <TabPanel height="100%" p={4}>
              <AnalysisRunsViewer selectedJob={selectedJob} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Footer action bar — only on Analysis/Scheduling tabs when there are changes */}
      {(activeTab === 0) && (hasFormChanges || isCreatingNew) && (
        <Box
          flexShrink={0}
          borderTop="1px"
          borderColor="gray.200"
          py={2}
          px={6}
          bg="white"
        >
          <HStack justify="space-between" align="center">
            <Text fontSize="md" color="gray.700" fontWeight="600" mb={0}>
              You have unsaved changes.
            </Text>
            <HStack spacing={3}>
              <Button
                colorScheme="green"
                size="md"
                onClick={handleSave}
                isLoading={isSaving}
                loadingText={isCreatingNew ? "Saving…" : "Updating…"}
                leftIcon={<Icon as={FaSave} boxSize={4} />}
                fontWeight="600"
                fontSize="md"
              >
                {isCreatingNew ? "Save" : "Update"}
              </Button>
              <Button
                colorScheme="red"
                variant="outline"
                size="md"
                onClick={handleCancel}
                isDisabled={isSaving}
                leftIcon={<Icon as={FaTimes} boxSize={4} />}
                fontWeight="600"
                fontSize="md"
              >
                {isCreatingNew ? "Cancel" : "Discard"}
              </Button>
            </HStack>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default JobViewComponent;
